/**
 * Assignment milestones: an ordered list of checkpoints on one assignment,
 * each owning its own unit tests and its own completion.
 *
 * Two rules are enforced here rather than in the UI, because a milestone's
 * tests describe the program only as it looked at that step:
 *
 *   1. One milestone is checked at a time — {@link selectCurrentMilestoneId}.
 *   2. Earning a milestone is permanent. Nothing in this module can express
 *      "un-earn"; the progress helpers only ever add keys.
 *
 * Progress lives in the course's existing per-user assignment progress
 * document, under the composite-key convention lesson slides already use —
 * see {@link buildMilestoneProgressKey}.
 *
 * Tracked in [#4556](https://github.com/CodeinPlace/cip/issues/4556).
 */

import {z} from 'zod';

/** Separator between an assignment id and a milestone id in a progress key. */
const MILESTONE_KEY_SEPARATOR = '/';

/** Prefix shared by every per-milestone prompt document id. */
const MILESTONE_PROMPT_DOC_PREFIX = 'milestone-';

/**
 * Schema for one milestone declared on an assignment's metadata.
 */
export const AssignmentMilestoneSchema = z.object({
  /**
   * Stable, per-assignment unique id. Part of the progress key and of the
   * IDE's `?milestone=` query parameter, so renaming one discards student
   * progress on that step.
   */
  id: z.string().min(1),
  /**
   * Label shown in the instructions pane, on the roadmap sub-dot and in the
   * test-results caption. Everything else the student needs to know lives in
   * the milestone's instructions document.
   */
  title: z.string().min(1),
});

/**
 * One milestone declared on an assignment's metadata.
 */
export type AssignmentMilestone = z.infer<typeof AssignmentMilestoneSchema>;

/**
 * Schema for an assignment's ordered milestone list.
 *
 * Order is the order the student works through them. Ids must be unique, or
 * two milestones would share one progress key.
 */
export const AssignmentMilestonesSchema = z
  .array(AssignmentMilestoneSchema)
  .refine(
    milestones =>
      new Set(milestones.map(milestone => milestone.id)).size ===
      milestones.length,
    {message: 'Milestone ids must be unique within an assignment'},
  );

/**
 * Builds the key a milestone's completion is stored under inside the per-user
 * assignment progress map.
 *
 * @param args - The assignment and milestone being addressed.
 * @param args.assignmentId - Assignment the milestone belongs to.
 * @param args.milestoneId - Milestone's `id` from its metadata entry.
 * @return The progress-map key for that milestone.
 * @throws If either id is empty, or if `assignmentId` contains the separator —
 *   both would produce a key that cannot be read back unambiguously.
 */
export function buildMilestoneProgressKey({
  assignmentId,
  milestoneId,
}: {
  assignmentId: string;
  milestoneId: string;
}): string {
  if (assignmentId === '' || milestoneId === '') {
    throw new Error(
      'buildMilestoneProgressKey requires a non-empty assignmentId and milestoneId',
    );
  }
  if (assignmentId.includes(MILESTONE_KEY_SEPARATOR)) {
    throw new Error(
      `Assignment id "${assignmentId}" contains "${MILESTONE_KEY_SEPARATOR}", ` +
        'which would make its milestone progress keys ambiguous',
    );
  }
  return `${assignmentId}${MILESTONE_KEY_SEPARATOR}${milestoneId}`;
}

/**
 * Document id, under an assignment's `docs` subcollection, holding one
 * milestone's instructions. The shared introduction stays in `prompt`.
 *
 * @param milestoneId - The milestone whose instructions are wanted.
 * @return The document id, e.g. `milestone-exact-matches`.
 * @throws If `milestoneId` is empty, which would produce a document nobody can
 *   find.
 */
export function buildMilestonePromptDocId(milestoneId: string): string {
  if (milestoneId === '') {
    throw new Error('buildMilestonePromptDocId requires a milestoneId');
  }
  return `${MILESTONE_PROMPT_DOC_PREFIX}${milestoneId}`;
}

/**
 * A read-only view of the per-user assignment progress map.
 *
 * Keys are either a bare assignment id (whole-assignment completion) or a
 * composite milestone key from {@link buildMilestoneProgressKey}. A missing
 * key and an explicit `false` both mean incomplete.
 */
export type MilestoneProgressLookup = Readonly<
  Record<string, boolean | undefined>
>;

/**
 * Reports whether the student has already earned a milestone.
 *
 * @param args - The progress map and the milestone being asked about.
 * @param args.progress - The per-user assignment progress map. Omitted, every
 *   milestone reads as unearned.
 * @param args.assignmentId - Assignment the milestone belongs to.
 * @param args.milestoneId - Milestone's `id`.
 * @return Whether that milestone is earned.
 * @throws Propagates from {@link buildMilestoneProgressKey} on malformed ids.
 */
export function isMilestoneEarned({
  progress,
  assignmentId,
  milestoneId,
}: {
  progress?: MilestoneProgressLookup;
  assignmentId: string;
  milestoneId: string;
}): boolean {
  if (!progress) {
    return false;
  }
  return (
    progress[buildMilestoneProgressKey({assignmentId, milestoneId})] === true
  );
}

/**
 * Reports whether a milestone is unlocked — whether the student may work on it
 * yet. The first is always unlocked; every later one unlocks when the
 * milestone immediately before it has been earned.
 *
 * Reading only the immediately preceding milestone keeps the rule monotone
 * across a gap in stored progress, which a student can have if a milestone was
 * inserted after they started.
 *
 * @param args - The ladder, the progress map and the milestone in question.
 * @param args.milestones - The assignment's ordered milestones.
 * @param args.progress - The per-user assignment progress map. Omitted, only
 *   the first milestone is unlocked.
 * @param args.assignmentId - Assignment the milestones belong to.
 * @param args.milestoneId - The milestone being asked about.
 * @return Whether it is unlocked. `false` for an id the assignment does not
 *   declare.
 * @throws Propagates from {@link buildMilestoneProgressKey} on malformed ids.
 */
export function isMilestoneUnlocked({
  milestones,
  progress,
  assignmentId,
  milestoneId,
}: {
  milestones: readonly AssignmentMilestone[];
  progress?: MilestoneProgressLookup;
  assignmentId: string;
  milestoneId: string;
}): boolean {
  const index = milestones.findIndex(milestone => milestone.id === milestoneId);
  if (index === -1) {
    return false;
  }
  if (index === 0) {
    return true;
  }
  return isMilestoneEarned({
    progress,
    assignmentId,
    milestoneId: milestones[index - 1].id,
  });
}

/**
 * Reports whether every declared milestone has been earned, which is when the
 * assignment itself is also marked complete.
 *
 * @param args - The milestone list and the progress map.
 * @param args.milestones - The assignment's ordered milestones. An empty list
 *   returns `false`: such an assignment is graded by the ordinary
 *   whole-assignment path instead.
 * @param args.progress - The per-user assignment progress map. Omitted, the
 *   result is `false`.
 * @param args.assignmentId - Assignment the milestones belong to.
 * @return Whether all milestones are earned.
 * @throws Propagates from {@link buildMilestoneProgressKey} on malformed ids.
 */
export function areAllMilestonesEarned({
  milestones,
  progress,
  assignmentId,
}: {
  milestones: readonly AssignmentMilestone[];
  progress?: MilestoneProgressLookup;
  assignmentId: string;
}): boolean {
  if (milestones.length === 0) {
    return false;
  }
  return milestones.every(milestone =>
    isMilestoneEarned({progress, assignmentId, milestoneId: milestone.id}),
  );
}

/**
 * Chooses which milestone the IDE should have selected.
 *
 * Precedence:
 *   1. `requestedMilestoneId`, when it names a declared milestone that is
 *      unlocked — including an already-earned one, to run its tests again.
 *   2. The first milestone not yet earned.
 *   3. The last milestone, when every one is earned.
 *
 * An unknown or still-locked request is ignored rather than treated as an
 * error: it arrives from a stale bookmark or a shared URL, and handing a
 * student a step whose spec assumes code they have not written is worse than
 * falling through to their real position.
 *
 * @param args - The milestone list, the progress map and any explicit request.
 * @param args.milestones - The assignment's ordered milestones.
 * @param args.progress - The per-user assignment progress map. Omitted, the
 *   first milestone is selected.
 * @param args.assignmentId - Assignment the milestones belong to.
 * @param args.requestedMilestoneId - An explicitly requested milestone.
 *   Omitted, unknown, or locked falls through to rules 2 and 3.
 * @return The selected milestone's id, or `undefined` when `milestones` is
 *   empty.
 * @throws Propagates from {@link buildMilestoneProgressKey} on malformed ids.
 */
export function selectCurrentMilestoneId({
  milestones,
  progress,
  assignmentId,
  requestedMilestoneId,
}: {
  milestones: readonly AssignmentMilestone[];
  progress?: MilestoneProgressLookup;
  assignmentId: string;
  requestedMilestoneId?: string;
}): string | undefined {
  if (milestones.length === 0) {
    return undefined;
  }
  const requested = milestones.find(
    milestone => milestone.id === requestedMilestoneId,
  );
  if (
    requested &&
    isMilestoneUnlocked({
      milestones,
      progress,
      assignmentId,
      milestoneId: requested.id,
    })
  ) {
    return requested.id;
  }
  const firstUnearned = milestones.find(
    milestone =>
      !isMilestoneEarned({progress, assignmentId, milestoneId: milestone.id}),
  );
  if (firstUnearned) {
    return firstUnearned.id;
  }
  return milestones[milestones.length - 1].id;
}

/**
 * The display state of one milestone on the IDE ladder and the roadmap.
 *
 * - `earned`: already banked, whether or not its tests would still pass.
 * - `current`: the selected milestone, which the check button will run.
 * - `unlocked`: reachable and not earned, but not selected.
 * - `locked`: the milestone before it is unearned, so it cannot be checked and
 *   its instructions stay hidden.
 */
export type MilestoneDisplayState =
  | 'earned'
  | 'current'
  | 'unlocked'
  | 'locked';

/**
 * One milestone paired with the state the UI should render it in.
 */
export interface MilestoneWithState {
  /** The milestone itself. */
  milestone: AssignmentMilestone;
  /** How to render it. */
  state: MilestoneDisplayState;
  /** 1-based position in the ladder, for labels like "Milestone 3 of 5". */
  position: number;
}

/**
 * Pairs each milestone with its display state, in ladder order.
 *
 * @param args - The milestone list, the progress map and the selection.
 * @param args.milestones - The assignment's ordered milestones.
 * @param args.progress - The per-user assignment progress map. Omitted, no
 *   milestone reads as earned.
 * @param args.assignmentId - Assignment the milestones belong to.
 * @param args.selectedMilestoneId - The selected milestone. Omitted, no entry
 *   is marked `current`.
 * @return One entry per milestone, in the declared order.
 * @throws Propagates from {@link buildMilestoneProgressKey} on malformed ids.
 */
export function buildMilestoneLadder({
  milestones,
  progress,
  assignmentId,
  selectedMilestoneId,
}: {
  milestones: readonly AssignmentMilestone[];
  progress?: MilestoneProgressLookup;
  assignmentId: string;
  selectedMilestoneId?: string;
}): MilestoneWithState[] {
  return milestones.map((milestone, index) => {
    const earned = isMilestoneEarned({
      progress,
      assignmentId,
      milestoneId: milestone.id,
    });
    const unlocked = isMilestoneUnlocked({
      milestones,
      progress,
      assignmentId,
      milestoneId: milestone.id,
    });
    // `current` beats `earned` so a re-selected finished step still shows what
    // the check button will act on. `earned` beats `locked` so a gap in stored
    // progress cannot hide a step the student has completed.
    const state: MilestoneDisplayState =
      milestone.id === selectedMilestoneId
        ? 'current'
        : earned
          ? 'earned'
          : unlocked
            ? 'unlocked'
            : 'locked';
    return {milestone, state, position: index + 1};
  });
}
